import { AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ApplicationState } from 'store/rootReducer';
import { MockServiceActionTypes } from 'store/mockServices/mockServiceActionTypes';
import { MockService } from 'types/rest/mockServiceTypes';
import * as actionTypes from 'constants/action-types';
import {
  EXPAND_SHUTTLE_TXN_ITEM,
  HTTP_RECORDING_SAVE_IN_PROGRESS,
  PANE_IS_LOADING,
  RECORDING_SAVE_IN_PROGRESS,
  RESET_SELECT_ALL,
  RESET_TRANSACTION_SHUTTLE,
  SET_ACTIVE_MESSAGING_TAB,
  UPDATE_SHUTTLE_ACTIVE_PAGE,
  UPDATE_SHUTTLE_MOVE_IN_PROGRESS,
  UPDATE_SHUTTLE_SEARCH_TERM,
  UPDATE_SHUTTLE_SORTING,
  UPDATE_SHUTTLE_TRANSACTION,
  UPDATE_SHUTTLE_TRANSACTION_SELECTION,
  UPDATE_SHUTTLE_TRANSACTIONS,
} from 'constants/action-types';
import axiosInstance from 'axios/axios-base';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { BlazeMeterApiResponse } from 'types/rest/blazeMeterApiResponse';
import { action } from 'typesafe-actions';
import _ from 'lodash';
import {
  createDraft,
  getExpandedMockService,
  mockServiceChanged,
  mockServicePatchAction,
} from 'store/mockServices/mockServiceList/mockServiceListActions';
import { MockServiceDetailsState } from 'store/mockServices/mockServiceDetail/mockServiceDetailTypes';
import { cacheControlHeaders, getErrorMessage, isCancelTokenErrorMessage } from 'utils/miscUtil';
import axios, { CancelToken, CancelTokenSource } from 'axios';
import { PanePosition, ShuttlePaneControls } from 'store/transactionShuttle/TransactionShuttleTypes';
import { DetailTabKey, MsDetailParametersTab } from 'store/mockServices/mockServiceTypes';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { Transaction, TransactionType } from 'types/rest/assetCatalogTypes';
import { SelectAllType } from '../../../components/blazeTable/BlazeTable.types';
import acApi from '../../../axios/ac/acApi';
import { AssignmentActionType } from '../../../components/transactionsShuttle/TransactionShuttle.types';
import { buildSortingParam } from '../../../utils/pagebleUtil';
import { SortConfiguration } from '../../../types/rest/paginationTypes';
import { AssetAssignmentBundle } from '../../../views/MockServices/Tabs/MockServices/SslConfiguration';
import { InspectionViewRequest, MessagingInspectionViewRequest } from '../../../types/rest/analyticsTypes';
import { PaneState } from '../../../components/shuttle/Shuttle';
import { pollTrackingService } from '../../../axios/axios-polling';
import { Link } from 'react-router-dom';
import React from 'react';

/**
 * Updates transactions for both panes in the transaction shuttle
 * @param mockService
 * @param transactionType http / messaging
 */
export const reloadTransactionShuttle: any = (
  mockService: MockService,
  transactionType: TransactionType,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    //Left pane
    dispatch(fetchTransactionsForShuttlePane(mockService, 'LEFT', transactionType));

    //Right pane
    dispatch(fetchTransactionsForShuttlePane(mockService, 'RIGHT', transactionType));
  };
};

export const updateTransactionsForShuttleAction = (
  type: PanePosition,
  totalResults: number,
  transactions: Transaction[],
) => {
  return action(UPDATE_SHUTTLE_TRANSACTIONS, {
    type: type,
    totalResults: totalResults,
    transactions: transactions,
  });
};

/**
 * Change tab with currently selected runner.
 * @param tabKey
 */
export const updateRunnerTab = (tabKey: string) => action(actionTypes.UPDATE_RUNNER_TAB, tabKey);

/**
 * Updates selected tab in http runner.
 * @param tabKey
 */
export const updateHttpRunnerTab = (tabKey: string) => action(actionTypes.HTTP_RUNNER_UPDATE_TAB, tabKey);

/**
 * Updates selected tab in messaging runner.
 * @param tabKey
 */
export const updateMessagingRunnerTab = (tabKey: string) => action(actionTypes.MESSAGING_RUNNER_UPDATE_TAB, tabKey);

/**
 * Updates selected tab in the recording configuration.
 * @param tabKey
 */
export const updateMessagingRecordedTab = (tabKey: string) => action(actionTypes.MESSAGING_RECORDED_UPDATE_TAB, tabKey);

/**
 * Change of the tab in the parameter detail.
 * @param tabKey
 */
export const mockServiceParameterTabChanged = (tabKey: MsDetailParametersTab) =>
  action(actionTypes.MOCK_SERVICE_PARAM_TAB_CHANGED, tabKey);

/**
 * Change of the tab in the virtual service detail.
 * @param tabKey
 */
export const mockServiceDetailTabChanged = (tabKey: DetailTabKey) =>
  action(actionTypes.MOCK_SERVICE_DETAIL_TAB_CHANGED, tabKey);

/**
 * Update recorded messages in the detail.
 * @param messages
 */
export const updateRecordedMessages = (messages: MessagingInspectionViewRequest[]) =>
  action(actionTypes.MESSAGING_UPDATE_RECORDED_MESSAGES, messages);

/**
 * Update recorded messages in the detail.
 * @param messages
 */
export const updateHttpRecordedMessages = (
  messages: MessagingInspectionViewRequest[] | InspectionViewRequest[], //TODO verify message type
) => action(actionTypes.HTTP_UPDATE_RECORDED_MESSAGES, messages);

/**
 * Update recorded messages in the detail.
 */
export const updatePollingStatus = (status: boolean) => action(actionTypes.MESSAGING_UPDATE_POLLING_STATUS, status);

/**
 * Update http recorded messages in the detail.
 */
export const updateHttpPollingStatus = (status: boolean) => action(actionTypes.HTTP_UPDATE_POLLING_STATUS, status);

/**
 * Update recording name
 */
export const updateRecordingName = (name: string) => action(actionTypes.MESSAGING_UPDATE_RECORDING_NAME, name);

/**
 * Updates the state of a recording shuttle pane.
 * @param {PanePosition} position - The position of the pane (LEFT or RIGHT).
 * @param {PaneState} state - The new state of the pane.
 */
export const updateRecordingShuttlePane = (position: PanePosition, state: PaneState) =>
  action(actionTypes.RECORDING_SHUTTLE_UPDATE_PANE, {
    position: position,
    state: state,
  });

export const updateRecordingShuttlePaneInProgress = (progress: {
  isMoveLeftInProgress?: boolean;
  isMoveRightInProgress?: boolean;
}) => action(actionTypes.RECORDING_SHUTTLE_UPDATE_IN_PROGRESS, progress);

/**
 * Gets all recordings that are not assigned to the mock service.
 * @param mockService - The mock service.
 * @param resetSelection - If true, the selection will be reset.
 */
export const fetchAvailableRecordings = (mockService: MockService, resetSelection?: boolean) => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    const leftPane = _.cloneDeep(getState().mockServiceDetails.recordingShuttle.leftPane);

    //Set Is Loading
    dispatch(updateRecordingShuttlePane('LEFT', { ...leftPane, isLoading: true }));

    let params: any = buildParams(leftPane);
    if (!mockService.isDraft) {
      params = { ...params, excludeServiceMockId: mockService.id, serviceId: mockService.serviceId };
    } else {
      params = { ...params, excludeServiceMockDraftId: mockService.id, serviceId: mockService.serviceId };
    }

    try {
      const response = (await acApi.recordings.getRecordings(params)).data;
      leftPane.totalResults = response.total;
      leftPane.data = response.result;
      leftPane.isLoading = false;
      leftPane.selected = resetSelection ? [] : leftPane.selected;
      dispatch(updateRecordingShuttlePane('LEFT', leftPane));
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot get list of recordings', message));
      dispatch(updateRecordingShuttlePane('LEFT', { ...leftPane, isLoading: false }));
    }
  };
};

/**
 * Builds the query parameters for fetching data for a shuttle pane.
 *
 * @param {PaneState} pane - The state of the pane.
 * @returns {Object} The query parameters.
 */
const buildParams = (pane: PaneState): object => {
  // Prepare to sort
  const sortParam = buildSortingParam(pane.sort);

  return {
    skip: pane.pageSize * (pane.activePage - 1),
    limit: pane.pageSize,
    filter: pane.searchTerm ? pane.searchTerm : undefined,
    sort: _.isNil(sortParam) ? '-id' : sortParam,
  };
};

/**
 * Prepares the patch object for recording assignments.
 * @param {PanePosition} position - The position of the pane (LEFT or RIGHT).
 * @param {number[]} recordingIds - The IDs of the recordings to be included or excluded.
 */
export const prepareRecordingPatch = (position: PanePosition, recordingIds: number[]) => {
  return {
    [position === 'RIGHT' ? 'includeRecordingIds' : 'excludeRecordingIds']: recordingIds,
  };
};

export const moveRecordingsThunk = (position: PanePosition, mockService: MockService, recordingIds: number[]) => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    //From Right Pane we are moving left
    if (position === 'RIGHT') {
      dispatch(updateRecordingShuttlePaneInProgress({ isMoveLeftInProgress: true }));
    } else {
      dispatch(updateRecordingShuttlePaneInProgress({ isMoveRightInProgress: true }));
    }

    //If it is not a draft, create it beforehand
    if (!mockService.isDraft) {
      const draft = await dispatch(createDraft(mockService));
      mockService.id = draft.id;
      mockService.isDraft = true;
    }

    try {
      await acApi.mockServiceDrafts.patchDraftById(mockService.id, prepareRecordingPatch(position, recordingIds));

      //Refresh panes after the move
      dispatch(fetchAvailableRecordings(mockService, true));
      dispatch(fetchAssignedRecordings(mockService, true));
    } catch (e) {
      const message = getErrorMessage(e);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot assign / de-assign recordings', message));
    } finally {
      //No progress is shown
      dispatch(updateRecordingShuttlePaneInProgress({ isMoveLeftInProgress: false, isMoveRightInProgress: false }));
    }
  };
};

export const updateRunTimeConfigThunk = (mockService: MockService, recordingId: number, config: any) => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    try {
      if (!mockService.isDraft) {
        const draft = await dispatch(createDraft(mockService));
        mockService.id = draft.id;
        mockService.isDraft = true;
      }

      await acApi.mockServiceDrafts.updateRecording(mockService.id, recordingId, config);
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot update runtime configuration', message));
    }
  };
};

/**
 * Gets recordings which are assigned to the mock service.
 * @param mockService - The mock service.
 * @param resetSelection - If true, the selection will be reset.
 */
export const fetchAssignedRecordings = (mockService: MockService, resetSelection?: boolean) => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    const rightPane = _.cloneDeep(getState().mockServiceDetails.recordingShuttle.rightPane);

    //Set Is Loading
    dispatch(updateRecordingShuttlePane('RIGHT', { ...rightPane, isLoading: true }));

    const params: any = buildParams(rightPane);
    let response = null;

    try {
      if (mockService.isDraft) {
        response = (await acApi.mockServiceDrafts.getRecordings(mockService.id, params)).data;
      } else {
        response = (await acApi.mockServices.getRecordings(mockService.id, params)).data;
      }

      rightPane.totalResults = response.total;
      rightPane.data = response.result;
      rightPane.isLoading = false;
      rightPane.selected = resetSelection ? [] : rightPane.selected;
      dispatch(updateRecordingShuttlePane('RIGHT', rightPane));
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot get list of recordings', message));
      dispatch(updateRecordingShuttlePane('RIGHT', { ...rightPane, isLoading: false }));
    }
  };
};

/**
 * Toggle Show More
 */
export const toggleShowMore = () => action(actionTypes.TOGGLE_SHOW_MORE);

/**
 * Resets shuttle to default state (= pagination, expanded items, selection...)
 */
export const resetTransactionShuttle = () => {
  return action(RESET_TRANSACTION_SHUTTLE);
};

/**
 * Resets mock service detail state.
 */
export const resetMockServiceDetail = () => action(actionTypes.RESET_MOCK_SERVICE_DETAIL);

export const updateShowPriorityModal = (isModalShown: boolean) =>
  action(actionTypes.UPDATE_SHOW_PRIORITY_MODAL, isModalShown);

export const setLoadingCertificates = (isLoading: boolean) =>
  action(actionTypes.SET_CERTIFICATES_IS_LOADING, isLoading);

export const setAssetBundle = (assetBundle: AssetAssignmentBundle) => action(actionTypes.SET_ASSET_BUNDLE, assetBundle);

/**
 * Sets active page in one of the shuttle panes.
 */
export const updateShuttleActivePage = (type: PanePosition, activePage: number) => {
  return action(UPDATE_SHUTTLE_ACTIVE_PAGE, {
    type: type,
    activePage: activePage,
  });
};

/**
 * Sets recording save in progress.
 */
export const setRecordingSaveInProgress = (isSaving: boolean) => {
  return action(RECORDING_SAVE_IN_PROGRESS, isSaving);
};

/**
 * Sets Http recording save in progress.
 */
export const setHttpRecordingSaveInProgress = (isSaving: boolean) => {
  return action(HTTP_RECORDING_SAVE_IN_PROGRESS, isSaving);
};

/**
 * Sets sorting in one of the shuttle panes.
 */
export const updateShuttleSorting = (type: PanePosition, sort: SortConfiguration) => {
  return action(UPDATE_SHUTTLE_SORTING, {
    type: type,
    sort: sort,
  });
};

/**
 * Updates selection
 */
export const updateShuttleTransactionSelection = (
  type: PanePosition,
  transactions: number[],
  selectAll?: SelectAllType,
) => {
  return action(UPDATE_SHUTTLE_TRANSACTION_SELECTION, {
    type: type,
    transactions: transactions,
    selectAll: selectAll,
  });
};

export const resetSelectAllInShuttlePanes = () => {
  return action(RESET_SELECT_ALL);
};

/**
 * Updates single transaction in a shuttle pane.
 */
export const updateShuttleTransaction = (type: PanePosition, transaction: Transaction) => {
  return action(UPDATE_SHUTTLE_TRANSACTION, {
    type: type,
    transaction: transaction,
  });
};

/**
 * Sets filter for pane
 */
export const updateShuttleSearchTerm = (type: PanePosition, searchTerm: string) => {
  return action(UPDATE_SHUTTLE_SEARCH_TERM, {
    type: type,
    searchTerm: searchTerm,
  });
};

/**
 * Updates transaction priority in the list of transactions in the shuttle by id.
 * @param transactionId
 * @param priority
 */
export const updateTransactionPriority = (transactionId: number, priority: number) =>
  action(actionTypes.UPDATE_TRANSACTION_PRIORITY, {
    transactionId: transactionId,
    priority: priority,
  });

/**
 * Update transaction field in the list of transactions in the shuttle.
 * @param transactionId
 * @param fieldName
 * @param value
 */
export const updateMockServiceTransactionAction = (transactionId: number, fieldName: string, value: string) =>
  action(actionTypes.UPDATE_MS_TRANSACTION_FIELD, {
    transactionId: transactionId,
    fieldName: fieldName,
    value: value,
  });

/**
 * Sets pane to loading state.
 * @param type
 * @param isLoading
 */
export const paneIsLoadingAction = (type: PanePosition, isLoading: boolean) => {
  return action(PANE_IS_LOADING, {
    type: type,
    isLoading: isLoading,
  });
};

export const setMessagingActiveTab = (activeTab: string) => {
  return action(SET_ACTIVE_MESSAGING_TAB, activeTab);
};

/**
 * Sets pane to loading state.
 * @param type
 * @param isInProgress
 */
export const moveInProgressAction = (type: AssignmentActionType, isInProgress: boolean) => {
  return action(UPDATE_SHUTTLE_MOVE_IN_PROGRESS, {
    type: type,
    inProgress: isInProgress,
  });
};

/**
 * Expands item in a transaction shuttle.
 * @param type
 * @param expandItem
 */
export const expandTransactionAction = (type: PanePosition, expandItem: number | null) => {
  return action(EXPAND_SHUTTLE_TXN_ITEM, {
    type: type,
    expandItem: expandItem,
  });
};

/**
 * Changes transaction assignment for virtual service.
 * @param mockService
 * @param assignmentActionType
 * @param transactionType
 * @param singleTx
 */
export const moveTransactions: any = (
  mockService: MockService,
  assignmentActionType: AssignmentActionType,
  transactionType: TransactionType,
  singleTx?: number,
  flowConfig?: string,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    const mockServiceDetailState: MockServiceDetailsState = _.cloneDeep(
      getState().mockServiceDetails as MockServiceDetailsState,
    );

    // Show in progress indicator
    await dispatch(moveInProgressAction(assignmentActionType, true));

    // Assign in backend
    let patchBody;
    let searchTerm;

    // We add explicit -1 to a selected list of transaction to tell the backend explicitly
    // the assignment action is bulk one.
    if (assignmentActionType === AssignmentActionType.ADD) {
      const isSelectAllActive = isSelectAllInPaneActive(mockServiceDetailState, 'LEFT');
      const transactionIds = isSelectAllActive ? [-1] : mockServiceDetailState.txnShuttle.leftPane.selected;
      patchBody = {
        includeIds: singleTx === undefined ? transactionIds : [singleTx],
        flowConfiguration: flowConfig,
      };

      if (mockServiceDetailState.txnShuttle.leftPane.searchTerm) {
        searchTerm = mockServiceDetailState.txnShuttle.leftPane.searchTerm;
      }
    } else {
      const isSelectAllActive = isSelectAllInPaneActive(mockServiceDetailState, 'RIGHT');
      const transactionIds = isSelectAllActive ? [-1] : mockServiceDetailState.txnShuttle.rightPane.selected;
      patchBody = {
        excludeIds: singleTx === undefined ? transactionIds : [singleTx],
      };

      if (mockServiceDetailState.txnShuttle.rightPane.searchTerm) {
        searchTerm = mockServiceDetailState.txnShuttle.rightPane.searchTerm;
      }
    }

    // If search term is available, append it to patch body
    if (!_.isNil(searchTerm)) {
      patchBody = { ...patchBody, filter: searchTerm };
    }

    //If it is not a draft, created it beforehand
    if (!mockService.isDraft) {
      const draft = await dispatch(createDraft(mockService));
      mockService.id = draft.id;
      mockService.isDraft = true;
    }
    const currentMockService = { ...mockService };
    const patchUrl = '/workspaces/{workspaceId}/service-mock-drafts/';
    try {
      const data: BlazeMeterApiResponse = (await axiosInstance.patch(patchUrl + mockService.id, patchBody)).data;
      currentMockService.mockServiceTransactions = data.result.mockServiceTransactions;
      currentMockService.metadata.badges = data.result.metadata.badges;
    } catch (error) {
      const msg = _.get(error, 'data.result.error', '');
      dispatch(showToastNotification(ToastType.Danger, 'Cannot assign transaction', msg));
    } finally {
      dispatch(moveInProgressAction(assignmentActionType, false));
      dispatch(mockServiceChanged(currentMockService));

      //Reload the shuttle
      dispatch(fetchTransactionsForShuttlePane(currentMockService, 'LEFT', transactionType));
      dispatch(fetchTransactionsForShuttlePane(currentMockService, 'RIGHT', transactionType));
    }
  };
};

const isSelectAllInPaneActive = (mockServiceDetailState: MockServiceDetailsState, type: PanePosition) => {
  const paneSelector = type == 'LEFT' ? 'leftPane' : 'rightPane';
  return mockServiceDetailState.txnShuttle[paneSelector].selectAll == 'ALL';
};

// Cancel token sources for axios calls
let cancelTokenSourceLeftPane: CancelTokenSource | null = null;
let cancelTokenSourceRightPane: CancelTokenSource | null = null;

/**
 * Fetches a transaction list for single pane.
 * @param mockService
 * @param paneType left / right txn pane
 * @param transactionType can be messaging / http type of the transaction
 */
export const fetchTransactionsForShuttlePane: any = (
  mockService: MockService,
  paneType: PanePosition,
  transactionType: TransactionType,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    // Show loading indicator
    dispatch(paneIsLoadingAction(paneType, true));

    //TODO - remove when done
    if (_.isNil(transactionType)) {
      showToastNotification(ToastType.Warning, 'Cannot get list of transactions', 'Transaction type is not defined');
    }

    //API differs for left / right pane
    const transactionsApi: string = resolveApi(mockService, paneType);

    //Get data from store
    const shuttleState = getState().mockServiceDetails.txnShuttle;
    const paneControls: ShuttlePaneControls =
      paneType === 'LEFT' ? { ...shuttleState.leftPane } : { ...shuttleState.rightPane };

    //Prepare to sort
    const sortParam = buildSortingParam(paneControls.sort);

    //Query params
    const params: any = {
      skip: paneControls.pageSize * (paneControls.activePage - 1),
      limit: paneControls.pageSize,
      serviceId: mockService.serviceId,
      sort: _.isNil(sortParam) ? '-id' : sortParam,
      filter: paneControls.searchTerm ? paneControls.searchTerm : null,
      type: transactionType,
    };

    // TODO - refactor it to be more generic
    // Cancel any previous axios get request for the pane if it is happening
    // otherwise client might get wrong data from previous request
    let token: CancelToken | undefined;
    if (paneType === 'LEFT') {
      // Cancel previous request for left pane - if any pending
      if (cancelTokenSourceLeftPane) {
        cancelTokenSourceLeftPane.cancel('Fetching Transactions cancelled for ' + paneType);
      }

      cancelTokenSourceLeftPane = axios.CancelToken.source();
      token = cancelTokenSourceLeftPane.token;
    } else if (paneType === 'RIGHT') {
      // Cancel previous request for right pane - if any pending
      if (cancelTokenSourceRightPane) {
        cancelTokenSourceRightPane.cancel('Fetching Transactions cancelled for ' + paneType);
      }
      cancelTokenSourceRightPane = axios.CancelToken.source();
      token = cancelTokenSourceRightPane.token;
    }

    //Get configuration from store
    try {
      const responseData: BlazeMeterApiResponse = (
        await axiosInstance.get(transactionsApi, {
          params: params,
          headers: cacheControlHeaders,
          cancelToken: token,
        })
      ).data;
      const transactions = responseData.result || [];
      const totalResults = responseData.total as number;

      // Update pane controls
      dispatch(updateTransactionsForShuttleAction(paneType, totalResults, transactions));

      // Go to the first page if necessary (when there are some results in the backend db,
      // but we do not have any on the current page)
      let activePage = paneControls.activePage;

      if (activePage > 1 && totalResults >= paneControls.pageSize && transactions.length === 0) {
        dispatch(updateShuttleActivePage(paneType, --activePage));
      }
    } catch (error) {
      const msg = getErrorMessage(error);
      if (!isCancelTokenErrorMessage(msg)) {
        dispatch(showToastNotification(ToastType.Danger, 'Cannot get list of transactions', msg));
      }
    } finally {
      dispatch(paneIsLoadingAction(paneType, false));
    }
  };
};

/**
 * Resolves api for left / right pane.
 * The Left pane contains all transactions within a particular service.
 * The Right pane contains transactions which are assigned to a particular virtual service template.
 * @param mockService
 * @param type
 */
export const resolveApi = (mockService: MockService, type: PanePosition) => {
  let leftPaneApi = `/workspaces/{workspaceId}/transactions?`;
  let rightPaneApi;

  if (mockService.isDraft === true) {
    leftPaneApi += `excludeServiceMockDraftId=${mockService.id}`;
    rightPaneApi = `/workspaces/{workspaceId}/service-mock-drafts/${mockService.id}/transactions`;
  } else {
    leftPaneApi += `excludeServiceMockId=${mockService.id}`;
    rightPaneApi = `/workspaces/{workspaceId}/service-mocks/${mockService.id}/transactions`;
  }

  return type === 'LEFT' ? leftPaneApi : rightPaneApi;
};

export const updateMockServiceAssets: any = (
  assetBundle: AssetAssignmentBundle,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    const mockService = _.cloneDeep(getExpandedMockService());
    mockService.assets = assetBundle.assets;
    mockService.configKeyAssets = assetBundle.configKeyAssets;
    dispatch(setAssetBundle(assetBundle));
    dispatch(mockServicePatchAction(mockService));
  };
};

/**
 * Get a list of assets for the expanded virtual service / draft based on the inner mockService detail state.
 * @param mockService
 */
export const getAssetsForMockService: any = (
  mockService: MockService,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    let resp;
    //Get a list of certificates
    try {
      if (mockService.isDraft) {
        resp = await acApi.mockServiceDrafts.getById(mockService.id);
      } else {
        resp = await acApi.mockServices.getById(mockService.id);
      }
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot get list of assets', message));
      return;
    }

    const assets = _.get(resp, 'data.result.assets');
    const configKeyAssets = _.get(resp, 'data.result.configKeyAssets');

    //Save to detail
    dispatch(
      setAssetBundle({
        assets: assets,
        configKeyAssets: configKeyAssets,
      }),
    );

    //Copy assets and assign them to an MS object in the list
    const ms = getExpandedMockService();
    if (ms) {
      ms.assets = assets;
      ms.configKeyAssets = configKeyAssets;
      dispatch(mockServiceChanged(ms));
    }
  };
};

interface SaveRecordingDto {
  messageIds: number[];
  serviceId: number;
  recordingName: string;
}

/**
 * Thunk action to save the recording.
 */
export const saveRecordingThunk = (
  recordingDto: SaveRecordingDto,
): ThunkAction<void, ApplicationState, null, AnyAction> => {
  return async dispatch => {
    try {
      dispatch(setRecordingSaveInProgress(true));
      const response = (await acApi.recordings.import(recordingDto)).data;
      const trackingId = response.result.trackingId;

      dispatch(showToastNotification(ToastType.Info, 'Recording is being saved. Please wait...'));

      const trackingResult = await pollTrackingService(trackingId, 240, 2000);
      const errorMsg = _.get(trackingResult, 'data.result.errors');
      if (!_.isEmpty(errorMsg)) {
        throw new Error('Cannot save recording. ' + errorMsg);
      }
      const link = (
        <Link to="/asset-catalog/recordings" className={'mt-4'}>
          Go to Recordings
        </Link>
      );
      dispatch(showToastNotification(ToastType.Success, 'Recording saved successfully.', link));
    } catch (error) {
      dispatch(showToastNotification(ToastType.Danger, 'Failed to save recording.'));
    } finally {
      dispatch(setRecordingSaveInProgress(false));
    }
  };
};

/**
 * Thunk action to convert the http recordings.
 */
export const saveHttpRecordingThunk = (
  recordingDto: { analyticIds: any[] },
  params: any,
): ThunkAction<void, ApplicationState, null, AnyAction> => {
  return async dispatch => {
    try {
      dispatch(setHttpRecordingSaveInProgress(true));
      const response = (await acApi.recordings.convertHttpToTransaction(recordingDto, params)).data;
      const trackingId = response.result.trackingId;

      dispatch(showToastNotification(ToastType.Info, 'Recording is being converted to transactions. Please wait...'));

      const trackingResult = await pollTrackingService(trackingId, 240, 2000);
      const errorMsg = _.get(trackingResult, 'data.result.errors');
      if (!_.isEmpty(errorMsg)) {
        throw new Error('Cannot convert recording. ' + errorMsg);
      }
      const link = (
        <Link to="/asset-catalog/transactions" className={'mt-4'}>
          Go to transactions
        </Link>
      );
      dispatch(showToastNotification(ToastType.Success, 'Conversion saved successfully.', link));
    } catch (error) {
      dispatch(showToastNotification(ToastType.Danger, 'Failed to convert recording.'));
    } finally {
      dispatch(setHttpRecordingSaveInProgress(false));
    }
  };
};
